// Assignments in the bell (shell/InboxBell.tsx): the assignments waiting on the person, on top of the popover — up to five rows, each the
// title, what is asked of the viewer (to accept · to revise · to review · a review with checks · for your final approval · to acknowledge ·
// a new date is asked · a question for you · the brief changed · declined, to reassign), acting for whom, and the due date — with "See all"
// to `/assignments`. Drawn in the bell's own classes (`.nlab`, `.ni`, `.nf`) and ./lists/glance.css. The badge adds `useAssignmentCount`.
// v2 (24 Oct 2026, J11 "decide on the go"): decisions come first; a decision that needs no checks shows the package's bottom line and
// Approve (or Acknowledge) in the row, confirmed in place before it is taken; a gate's review opens its page. "Brief me" opens the portfolio.
import { useState } from "react";
import { Link } from "react-router";
import { Check, ChevronLeft, ChevronRight, CircleAlert, ClipboardList, Sunrise } from "lucide-react";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { toast } from "@ncp/ui";
import { useAssignmentActions, useAssignments } from "./api";
import { assignmentTitle, personName, type AssignmentListItem } from "./model";
import { groupByAction, inlineDecision, resultReason } from "./lists/lists";
import "./lists/glance.css";

const MAX = 5;
type K = (key: string, o?: Record<string, unknown>) => string;

/** Approve or Acknowledge from the bell: the button, then Confirm · Cancel in place; a refusal stays on the row with its reason. */
function InlineDecide({ a, k, lang }: { a: AssignmentListItem; k: K; lang: "en" | "ar" }) {
  const decision = inlineDecision(a)!;
  const { decideBatch } = useAssignmentActions();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = decision === "acknowledge" ? k("lists.decide.acknowledge") : k("lists.decide.approve");
  const run = () => decideBatch.mutate({ items: [{ id: a.id, revision: a.revision }] }, {
    onSuccess: ([r]) => {
      if (r?.ok) {
        setConfirming(false);
        toast.success(decision === "acknowledge" ? k("lists.bell.acknowledged") : k("lists.bell.approved"), { description: a.reference });
      } else {
        setConfirming(false);
        setError((r && resultReason(r, lang)) || k("failed"));
      }
    },
    onError: () => { setConfirming(false); setError(k("failed")); },
  });
  return (
    <div className="asg-bni-acts">
      {error ? <span className="err" role="alert"><CircleAlert aria-hidden />{error}</span> : null}
      {confirming ? (
        <>
          <button type="button" className="ec-btn plain sm" onClick={() => setConfirming(false)} disabled={decideBatch.isPending}>{k("lists.bell.cancel")}</button>
          <button type="button" className="ec-btn primary sm" onClick={run} disabled={decideBatch.isPending} aria-busy={decideBatch.isPending || undefined}
            aria-label={`${k("lists.bell.confirm")} · ${label} · ${a.reference}`}>
            <Check aria-hidden />{k("lists.bell.confirm")}
          </button>
        </>
      ) : (
        <button type="button" className="ec-btn secondary sm" onClick={() => { setError(null); setConfirming(true); }} aria-label={`${label} · ${a.reference}`}>{label}</button>
      )}
    </div>
  );
}

export function AssignmentsBellSection({ enabled, onGo }: { enabled: boolean; onGo: (href: string) => void }) {
  const { t } = useTranslation();
  const { locale, dir } = useLocale();
  const lang = locale === "ar" ? "ar" : "en";
  const q = useAssignments("action", 50, enabled);
  const all = q.data ?? [];
  if (!all.length) return null;
  const tr = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  const k: K = (key, o) => tr(`assistant:econic.assignments.${key}`, o);
  const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;
  // decisions first, then the other asks, each group in the server's order (by due date)
  const rows = groupByAction(all).flatMap((g) => g.items);
  return (
    <div className="flex flex-col" data-econic-bell-assignments="">
      <p className="nlab">{k("bell.title")}</p>
      {rows.slice(0, MAX).map((a) => {
        const due = a.dueAt ? formatDate(a.dueAt, lang) : null;
        const decide = inlineDecision(a) !== null;
        const acting = a.actingFor ? k("lists.bell.actingFor", { name: personName(a.actingFor, lang) }) : null;
        return (
          <div key={a.id} className="asg-bni" data-decide={decide ? "" : undefined}>
            <button type="button" className="ni" onClick={() => onGo(`/assignments/${a.id}`)}>
              <span className="u" aria-hidden />
              <ClipboardList aria-hidden />
              <span className="tx">
                <span className="tt" dir="auto">{assignmentTitle(a, lang)}</span>
                <span className="st">
                  {[acting, a.myAction ? k(`lists.asked.${a.myAction}`) : null, due ? k("row.due", { date: due }) : null].filter(Boolean).join(" · ")}
                  {a.overdue ? <span className="late"> · {k("days.late", { count: Math.abs(a.workingDaysLeft ?? 0) })}</span> : null}
                </span>
                {decide && a.bottomLine ? <span className="bl" dir="auto">{a.bottomLine}</span> : null}
              </span>
            </button>
            {decide ? <InlineDecide a={a} k={k} lang={lang} /> : null}
          </div>
        );
      })}
      <div className="nf asg-bnf">
        <Link to="/assignments?view=portfolio" className="brief" onClick={(e) => { e.preventDefault(); onGo("/assignments?view=portfolio"); }}>
          <Sunrise aria-hidden />{k("lists.bell.briefMe")}
        </Link>
        <span className="end">
          {all.length > MAX ? <span className="n">{`${MAX} / ${all.length}`}</span> : null}
          <Link to="/assignments" onClick={(e) => { e.preventDefault(); onGo("/assignments"); }}>{k("bell.seeAll")}<Chevron aria-hidden /></Link>
        </span>
      </div>
    </div>
  );
}
